import { useEffect, useState } from 'react';

import type { TraceHeaders } from '../../../../ai-answer-dialog/types';
import { createAtlAttributionHeader } from '../../../../common/fetch/atl-attribution-header';
import { logger } from '../../../../common/utils/logger';
import { ASSISTANCE_SERVICE_API_BASE_URL } from '../../../../constants';
import type { JiraAgentGenerateJqlRequest, JiraAgentGenerateJqlResponse } from './types';

const JIRA_AGENT_GENERATE_JQL_PATH = '/api/rovo/v1/jira-ticket-retrieval/generate-jql';
const JIRA_AGENT_GENERATE_JQL_URL = `${ASSISTANCE_SERVICE_API_BASE_URL}${JIRA_AGENT_GENERATE_JQL_PATH}`;

type FetchJiraAgentGenerateJqlOptions = {
	request: JiraAgentGenerateJqlRequest;
	workspaceId: string;
	cloudId: string;
	product: string;
	traceHeaders: TraceHeaders | undefined;
};

async function fetchJiraAgentGenerateJql({
	request,
	workspaceId,
	cloudId,
	product,
	traceHeaders,
}: FetchJiraAgentGenerateJqlOptions): Promise<JiraAgentGenerateJqlResponse> {
	const response = await fetch(JIRA_AGENT_GENERATE_JQL_URL, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Product': product,
			'X-Experience-Id': 'sain',
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: cloudId,
				product,
			}),
			...traceHeaders,
		},
		body: JSON.stringify(request),
	});

	if (!response.ok) {
		throw new Error(`Jira Agent generate-jql failed: ${response.status} ${response.statusText}`);
	}

	const data: JiraAgentGenerateJqlResponse = await response.json();
	return data;
}

export type UseJiraAgentParams = {
	/** Natural language query to convert to JQL. When empty, no request is sent. */
	query: string;
	cloudId: string;
	workspaceId: string;
	product: string;
	traceHeaders: TraceHeaders | undefined;
	/** When false, no request is sent and the hook returns idle state. Default true. */
	enabled?: boolean;
};

export type UseJiraAgentResult = {
	jql: string | undefined;
	loading: boolean;
	error: boolean;
};

/**
 * Hook to integrate with the Jira Agent NLQ2JQL API.
 * Calls POST /api/rovo/v1/jira-ticket-retrieval/generate-jql with body { query }.
 */
export const useJiraAgent = ({
	query,
	cloudId,
	workspaceId,
	product,
	traceHeaders,
	enabled = true,
}: UseJiraAgentParams): UseJiraAgentResult => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [jql, setJql] = useState<string | undefined>();

	useEffect(() => {
		if (!enabled) {
			setLoading(false);
			setError(false);
			setJql(undefined);
			return;
		}
		if (!query.trim() || !cloudId) {
			setLoading(false);
			setError(false);
			setJql(undefined);
			return;
		}

		let cancelled = false;

		const fetchData = async () => {
			setLoading(true);
			setError(false);
			try {
				const data = await fetchJiraAgentGenerateJql({
					request: { query: query.trim() },
					workspaceId,
					cloudId,
					product,
					traceHeaders,
				});

				if (cancelled) {
					return;
				}

				const generatedJql = 'JQL' in data ? data.JQL : undefined;
				const error = 'error' in data ? data.error : undefined;
				if (!generatedJql || error) {
					setError(true);
					setJql(undefined);
					return;
				}
				setJql(generatedJql);
				setError(false);
			} catch (e) {
				if (!cancelled) {
					logger.error({ error: e, functionName: 'useJiraAgent' });
					setError(true);
					setJql(undefined);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchData();
		return () => {
			cancelled = true;
		};
	}, [enabled, query, cloudId, workspaceId, product, traceHeaders]);

	return {
		jql,
		loading,
		error,
	};
};
